/**
 * Iter 67ca — Vendor Documents tab.
 *
 * Two surfaces in one panel:
 *  1) Your business documents — vendor uploads registration, VAT, license,
 *     insurance, ID, etc. Admin reviews each one. Vendor can re-upload
 *     after a rejection but can't delete approved ones.
 *  2) Admin-issued documents & contracts — read-only catalog of items the
 *     admin has uploaded to the vendor's profile. When `requires_signature`
 *     is set, the vendor must download, sign, and upload the signed copy
 *     back. Status badges show the journey: Issued → Awaiting signature →
 *     Signed.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import api, { API, fileUrl, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  FileText, Upload, Loader2, Trash2, Download, CheckCircle2, XCircle, Clock,
  ShieldCheck, FileSignature, FileWarning, Plus, AlertTriangle,
} from "lucide-react";

const STATUS_STYLE = {
  pending_review:     { label: "Pending review",      icon: Clock,         className: "bg-amber-50 text-amber-800 border-amber-200" },
  approved:           { label: "Approved",            icon: CheckCircle2,  className: "bg-emerald-50 text-emerald-800 border-emerald-200" },
  rejected:           { label: "Rejected",            icon: XCircle,       className: "bg-rose-50 text-rose-800 border-rose-200" },
  issued:             { label: "Issued by admin",     icon: FileText,      className: "bg-indigo-50 text-indigo-800 border-indigo-200" },
  awaiting_signature: { label: "Awaiting signature",  icon: FileSignature, className: "bg-amber-50 text-amber-800 border-amber-200" },
  signed:             { label: "Signed",              icon: CheckCircle2,  className: "bg-emerald-50 text-emerald-800 border-emerald-200" },
};

function _resolveSrc(path) {
  if (!path) return null;
  return /^https?:\/\//i.test(path) ? path : fileUrl(path);
}

function _StatusPill({ status, testId }) {
  const s = STATUS_STYLE[status] || { label: status, icon: FileText, className: "bg-neutral-100 text-neutral-700 border-neutral-200" };
  return (
    <Badge variant="outline" className={`inline-flex items-center gap-1 ${s.className} text-[10px] font-bold uppercase tracking-wider`} data-testid={testId}>
      <s.icon className="h-3 w-3" />
      {s.label}
    </Badge>
  );
}

async function _uploadFile(file) {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
  return data; // { path, content_type, filename }
}

export default function VendorDocumentsTab() {
  const [categories, setCategories] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draft, setDraft] = useState({ category: "registration", title: "", file: null, notes: "" });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const signFileRefs = useRef({}); // per-doc-id refs for signature upload

  const load = async () => {
    setLoading(true);
    try {
      const [c, r] = await Promise.all([
        api.get("/meta/vendor-document-categories"),
        api.get("/vendor/documents"),
      ]);
      setCategories(c.data || []);
      setRows(r.data || []);
    } catch (err) {
      toast.error(formatApiError(err) || "Could not load documents.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const ownRows = useMemo(() => rows.filter((r) => r.owner === "vendor"), [rows]);
  const adminRows = useMemo(() => rows.filter((r) => r.owner === "admin"), [rows]);
  const _categoryLabel = (key) => categories.find((c) => c.key === key)?.label || key;

  const onOpenCreate = () => {
    setDraft({ category: "registration", title: "", file: null, notes: "" });
    setDialogOpen(true);
  };

  const onPickFile = () => fileRef.current?.click();
  const onFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setDraft((d) => ({ ...d, file: f, title: d.title || f.name.replace(/\.[^.]+$/, "") }));
  };

  const onSave = async () => {
    if (!draft.title.trim()) { toast.error("Title is required."); return; }
    if (!draft.file) { toast.error("Pick a file to upload."); return; }
    setSaving(true);
    try {
      setUploading(true);
      const up = await _uploadFile(draft.file);
      setUploading(false);
      await api.post("/vendor/documents", {
        category: draft.category,
        title: draft.title.trim(),
        file_path: up.path,
        original_filename: up.filename,
        content_type: up.content_type,
        size: up.size,
        notes: draft.notes || null,
      });
      toast.success("Document uploaded — awaiting admin review.");
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err) || "Save failed.");
    } finally {
      setSaving(false);
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const onDelete = async (r) => {
    if (!window.confirm(`Delete "${r.title}"?`)) return;
    try {
      await api.delete(`/vendor/documents/${r.id}`);
      toast.success("Document removed.");
      load();
    } catch (err) {
      toast.error(formatApiError(err) || "Delete failed.");
    }
  };

  const onPickSignature = (docId) => {
    const el = signFileRefs.current[docId];
    if (el) el.click();
  };

  const onSignatureChange = async (docId, e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    try {
      const up = await _uploadFile(f);
      await api.post(`/vendor/documents/${docId}/sign`, {
        file_path: up.path,
        original_filename: up.filename,
        content_type: up.content_type,
      });
      toast.success("Signed copy uploaded.");
      load();
    } catch (err) {
      toast.error(formatApiError(err) || "Upload failed.");
    } finally {
      if (signFileRefs.current[docId]) signFileRefs.current[docId].value = "";
    }
  };

  return (
    <div className="space-y-8" data-testid="vendor-documents-tab">
      {/* Header */}
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-display text-xl font-medium flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-indigo-600" /> Business profile & documents
          </h3>
          <p className="text-sm text-neutral-500 max-w-2xl mt-1">
            Upload your business registration, tax/VAT certificate, license, and insurance documents.
            Admin reviews each one before your listings are made public.
          </p>
        </div>
        <Button onClick={onOpenCreate} className="bg-indigo-600 hover:bg-indigo-700" data-testid="vendor-docs-add">
          <Upload className="h-4 w-4 mr-1.5" /> Upload document
        </Button>
      </div>

      {/* Your uploads */}
      <div className="rounded-2xl border border-border bg-white overflow-hidden">
        <div className="px-5 py-3 border-b border-border flex items-center justify-between">
          <div className="font-semibold text-sm flex items-center gap-2">
            <FileText className="h-4 w-4 text-neutral-500" /> Your documents
          </div>
          <Badge variant="outline" className="text-[10px]">{ownRows.length} item{ownRows.length === 1 ? "" : "s"}</Badge>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="vendor-own-docs-table">
            <thead className="bg-neutral-50 border-b border-border text-[10px] uppercase tracking-wider text-neutral-600">
              <tr>
                <th className="text-left px-4 py-3 w-44">Category</th>
                <th className="text-left px-4 py-3">Title</th>
                <th className="text-left px-4 py-3 w-44">Status</th>
                <th className="text-left px-4 py-3 w-32">Uploaded</th>
                <th className="w-32"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (<tr><td colSpan={5} className="px-4 py-10 text-center text-neutral-500"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading…</td></tr>)}
              {!loading && ownRows.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-neutral-500">
                  No documents yet. Click <strong>Upload document</strong> to add your registration, VAT, license, or insurance papers.
                </td></tr>
              )}
              {!loading && ownRows.map((r) => (
                <tr key={r.id} className="border-b border-border last:border-0 hover:bg-neutral-50/60" data-testid={`vendor-doc-${r.id}`}>
                  <td className="px-4 py-3">
                    <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200 uppercase tracking-wider text-[10px] font-bold">
                      {_categoryLabel(r.category)}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium">{r.title}</div>
                    {r.admin_note && (
                      <div className="text-[11px] text-neutral-500 mt-0.5 flex items-start gap-1">
                        <span className="font-semibold text-neutral-700">Admin note:</span> <span>{r.admin_note}</span>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3"><_StatusPill status={r.status} testId={`vendor-doc-${r.id}-status`} /></td>
                  <td className="px-4 py-3 text-xs tabular-nums text-neutral-600">{(r.created_at || "").slice(0, 10)}</td>
                  <td className="px-2 py-3 text-right whitespace-nowrap">
                    <Button asChild variant="ghost" size="icon" className="h-8 w-8" title="Download">
                      <a href={_resolveSrc(r.file_path)} target="_blank" rel="noopener noreferrer" data-testid={`vendor-doc-${r.id}-download`}>
                        <Download className="h-3.5 w-3.5 text-indigo-600" />
                      </a>
                    </Button>
                    {r.status !== "approved" && (
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-rose-600" onClick={() => onDelete(r)} data-testid={`vendor-doc-${r.id}-delete`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Admin-issued documents */}
      <div className="rounded-2xl border border-border bg-white overflow-hidden">
        <div className="px-5 py-3 border-b border-border flex items-center justify-between">
          <div className="font-semibold text-sm flex items-center gap-2">
            <FileSignature className="h-4 w-4 text-indigo-600" /> Contracts & documents from RentalCar.al
          </div>
          <Badge variant="outline" className="text-[10px]">{adminRows.length} item{adminRows.length === 1 ? "" : "s"}</Badge>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="vendor-admin-docs-table">
            <thead className="bg-neutral-50 border-b border-border text-[10px] uppercase tracking-wider text-neutral-600">
              <tr>
                <th className="text-left px-4 py-3 w-44">Category</th>
                <th className="text-left px-4 py-3">Title</th>
                <th className="text-left px-4 py-3 w-44">Status</th>
                <th className="text-left px-4 py-3 w-32">Issued</th>
                <th className="w-56"></th>
              </tr>
            </thead>
            <tbody>
              {!loading && adminRows.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-neutral-500">
                  No contracts yet — RentalCar.al will publish them here when ready.
                </td></tr>
              )}
              {adminRows.map((r) => (
                <tr key={r.id} className="border-b border-border last:border-0 hover:bg-neutral-50/60" data-testid={`vendor-admin-doc-${r.id}`}>
                  <td className="px-4 py-3">
                    <Badge variant="outline" className="bg-indigo-50 text-indigo-800 border-indigo-200 uppercase tracking-wider text-[10px] font-bold">
                      {_categoryLabel(r.category)}
                    </Badge>
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium">{r.title}</div>
                    {r.is_required_for_activation && (
                      <div className="text-[11px] text-amber-700 mt-0.5 flex items-center gap-1">
                        <AlertTriangle className="h-3 w-3" /> Required for activation
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3"><_StatusPill status={r.status} testId={`vendor-admin-doc-${r.id}-status`} /></td>
                  <td className="px-4 py-3 text-xs tabular-nums text-neutral-600">{(r.created_at || "").slice(0, 10)}</td>
                  <td className="px-2 py-3 text-right whitespace-nowrap">
                    <Button asChild variant="ghost" size="sm" className="h-8 px-2">
                      <a href={_resolveSrc(r.file_path)} target="_blank" rel="noopener noreferrer" data-testid={`vendor-admin-doc-${r.id}-download`}>
                        <Download className="h-3.5 w-3.5 mr-1" /> Download
                      </a>
                    </Button>
                    {r.requires_signature && r.status !== "signed" && (
                      <>
                        <Button
                          variant="default"
                          size="sm"
                          className="h-8 bg-indigo-600 hover:bg-indigo-700 ml-1"
                          onClick={() => onPickSignature(r.id)}
                          data-testid={`vendor-admin-doc-${r.id}-sign`}
                        >
                          <FileSignature className="h-3.5 w-3.5 mr-1" /> Upload signed
                        </Button>
                        <input
                          ref={(el) => { signFileRefs.current[r.id] = el; }}
                          type="file"
                          accept="application/pdf,image/*"
                          onChange={(e) => onSignatureChange(r.id, e)}
                          className="hidden"
                        />
                      </>
                    )}
                    {r.status === "signed" && r.signed_file_path && (
                      <Button asChild variant="outline" size="sm" className="h-8 px-2 ml-1">
                        <a href={_resolveSrc(r.signed_file_path)} target="_blank" rel="noopener noreferrer" data-testid={`vendor-admin-doc-${r.id}-signed-download`}>
                          <Download className="h-3.5 w-3.5 mr-1" /> Signed copy
                        </a>
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Upload dialog */}
      <Dialog open={dialogOpen} onOpenChange={(o) => !saving && setDialogOpen(o)}>
        <DialogContent className="sm:max-w-lg" data-testid="vendor-doc-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-indigo-600" /> Upload document
            </DialogTitle>
            <DialogDescription>Will be submitted for admin review. Approved documents can&apos;t be deleted directly — ask an admin to revoke.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Category</Label>
              <Select value={draft.category} onValueChange={(v) => setDraft({ ...draft, category: v })}>
                <SelectTrigger className="mt-1" data-testid="vendor-doc-dialog-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {categories.map((c) => <SelectItem key={c.key} value={c.key}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Title</Label>
              <Input
                value={draft.title}
                onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                placeholder="e.g. Business registration 2026"
                className="mt-1"
                data-testid="vendor-doc-dialog-title"
              />
            </div>
            <div>
              <Label>File</Label>
              <div className="mt-1 flex items-center gap-2">
                <Button variant="outline" type="button" onClick={onPickFile} data-testid="vendor-doc-dialog-pick">
                  <Upload className="h-4 w-4 mr-1.5" /> Choose file
                </Button>
                <input ref={fileRef} type="file" accept="application/pdf,image/*" onChange={onFileChange} className="hidden" />
                {draft.file && <span className="text-xs text-neutral-600 truncate">{draft.file.name}</span>}
              </div>
              <p className="text-[11px] text-neutral-500 mt-1">PDF, JPG, PNG, WebP up to 8MB.</p>
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                value={draft.notes}
                onChange={(e) => setDraft({ ...draft, notes: e.target.value })}
                rows={2}
                placeholder="Any context the admin should know"
                className="mt-1"
                data-testid="vendor-doc-dialog-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogOpen(false)} disabled={saving}>Cancel</Button>
            <Button onClick={onSave} disabled={saving || !draft.file} className="bg-indigo-600 hover:bg-indigo-700" data-testid="vendor-doc-dialog-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Upload className="h-4 w-4 mr-1.5" />}
              {uploading ? "Uploading…" : "Submit for review"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
